import React, { useState } from 'react'
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import { read, utils } from 'xlsx'

import CircularLoading from '../../../../../../../components/CircularProgress'
import useAxios, { axiosInstance } from '../../../../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../../../../types/pagination'
import SelectSearch, {
  IOptionSelect,
} from '../../../../../../../components/SelectSearch'
import { IDistributionChannel } from '../../../../../../../types/distribution-channel'
import { validateMinMaxValue } from '../../../../../../../utils/validateMinMaxValue'
import { IPipeline } from '../../../../../../../types/pipeline'
import GridOnIcon from '@mui/icons-material/GridOn'
import { message } from 'antd'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../../constants/errors'
import ChannelSelectSearch from '../../../../../../../components/SelectSearch/ChannelSelectSearch'
import PipelineSelectSearch from '../../../../../../../components/SelectSearch/PipelineSelectSearch'
import { chunk, isEmpty, uniq } from 'lodash'
import { delayTime, numberFormat } from '../../../../../../../utils'

interface ILeadImportCustomerRegisterFormModalProps {
  isOpen: boolean
  onClose: (visible: boolean) => void
  onUpdateLeadListSuccess: () => void
}

interface ILeadImportCustomerRegisterFormModal {
  file: File | any
  pipeline: string
  distributionChannel: string
  expiredDays: number
  ignoreBlacklist?: string
}

interface ILeadExcel {
  name: string
  address: string
  subdistrict: string
  amphur: string
  province: string
  zipcode: string
  email: string
  phone: string
  addby: string
  facebook: string
  channel: string
  note: string
}

const getAddressFormValidationSchema = () => {
  return Yup.object({
    file: Yup.mixed(),
    // pipeline: Yup.string().required('กรุณาเลือก pipeline'),
    // distributionChannel: Yup.string().required('กรุณาเลือกช่องทาง'),
    expiredDays: Yup.number()
      .min(1, 'กรุณากรอกจำนวนวันที่หมดอายุมากกว่า 1 วัน')
      .max(1000, 'กรุณากรอกจำนวนวันที่หมดอายุน้อยกว่า 1000 วัน')
      .required('กรุณากรอกจำนวนวันที่หมดอายุ'),
  })
}

export const LeadImportCustomerRegisterFormModal = (
  props: ILeadImportCustomerRegisterFormModalProps,
) => {
  const { isOpen, onClose, onUpdateLeadListSuccess } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchPipeline, setSearchPipeline] = useState<string>('')
  const [popupLoading, setPopupLoading] = useState<boolean>(false)
  const [popupMessage, setPopupMessage] = useState<string>('')
  const [searchDistributionChannel, setSearchDistributionChannel] =
    useState<string>('')
  const [leadExcelList, setLeadExcelList] = useState<ILeadExcel[]>([])

  const formik = useFormik<ILeadImportCustomerRegisterFormModal>({
    initialValues: {
      file: null,
      pipeline: '',
      distributionChannel: '',
      expiredDays: 1,
      ignoreBlacklist: 'false',
    } as ILeadImportCustomerRegisterFormModal,
    validationSchema: getAddressFormValidationSchema(),
    onSubmit: async (values: ILeadImportCustomerRegisterFormModal) => {
      if (!values.pipeline) {
        message.error('กรุณาเลือก pipeline')
        return
      }

      setIsLoading(true)
      const perItems = 1
      const chucks = chunk(leadExcelList, perItems)
      let responses: any[] = []
      let errorsMessage = ''
      let i = 0
      let chunkCount = 0
      for (const chunk of chucks) {
        chunkCount += chunk.length
        try {
          const response = await axiosInstance.post<
            { status: any; message?: string }[]
          >('/lead/import-excel', {
            leads: chunk,
            pipelineObjectId: values.pipeline,
            channelObjectId: values.distributionChannel,
            expiredDays: values.expiredDays,
            ignoreBlacklist: values.ignoreBlacklist === 'true',
          })
          responses = [...responses, ...response.data]
        } catch (e: any) {
          const messageError =
            ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] || ''
          // console.log(e.response?.data)

          const rowsNumber = Number(e.response?.data?.rows)
          const row = rowsNumber ? `แถวที่ ${i * perItems + rowsNumber}` : ''

          errorsMessage += `${messageError} ${row}\n`
        }
        const message = `กำลังนำเข้าข้อมูล... ${numberFormat(
          chunkCount,
        )}/${numberFormat(chucks.length)}`
        setPopupMessage(message)
        i++
        await delayTime(1500)
      }

      if (isEmpty(responses)) {
        if (errorsMessage) {
          const errs = uniq(errorsMessage.split('\n')).filter(Boolean)
          message.open({
            type: 'error',
            content: (
              <div>
                นำเข้าข้อมูลไม่สำเร็จ:
                {errs.map((item) => {
                  return <div>- {item}</div>
                })}
              </div>
            ),
          })
        } else {
          message.error('นำเข้าข้อมูลไม่สำเร็จ')
        }
        onUpdateLeadListSuccess()
        onClose(false)
        setIsLoading(false)
        return
      }

      if (errorsMessage) {
        const errs = uniq(errorsMessage.split('\n')).filter(Boolean)
        message.open({
          type: 'error',
          content: (
            <div>
              นำเข้าข้อมูลไม่สำเร็จ:
              {errs.map((item) => {
                return <div>- {item}</div>
              })}
            </div>
          ),
        })
        onUpdateLeadListSuccess()
        onClose(false)
        setIsLoading(false)
        return
      }

      onUpdateLeadListSuccess()
      onClose(false)
      setIsLoading(false)

      if (responses.filter((res) => res.status === 'success').length > 0) {
        message.success(
          `นำเข้าข้อมูลสำเร็จ ${
            responses.filter((res) => res.status === 'success').length
          } รายการ`,
        )
      }
      if (responses.filter((res) => res.status === 'warning').length > 0) {
        message.error(
          `นำเข้าข้อมูลไม่สำเร็จ ${
            responses.filter((res) => res.status === 'warning').length
          }`,
        )
      }

      return

      try {
        const response = await axiosInstance.post<
          { status: any; message?: string }[]
        >('/lead/import-excel', {
          leads: leadExcelList,
          pipelineObjectId: values.pipeline,
          channelObjectId: values.distributionChannel,
          expiredDays: values.expiredDays,
          ignoreBlacklist: values.ignoreBlacklist === 'true',
        })
        onUpdateLeadListSuccess()
        onClose(false)
        if (
          response.data.filter((res) => res.status === 'success').length > 0
        ) {
          message.success(
            `นำเข้าข้อมูลสำเร็จ ${
              response.data.filter((res) => res.status === 'success').length
            } รายการ`,
          )
        }
        if (
          response.data.filter((res) => res.status === 'warning').length > 0
        ) {
          message.error(
            `นำเข้าข้อมูลไม่สำเร็จ ${
              response.data.filter((res) => res.status === 'warning').length
            }`,
          )
        }
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        // console.log(e.response?.data)
        const row = e.response?.data?.rows
          ? `แถวที่ ${e.response.data.rows}`
          : ''
        message.error(`${messageError} ${row}`)
        // message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  const pipelineList = useAxios<PaginationResponseInterface<IPipeline>>({
    method: 'GET',
    url: '/pipeline',
    params: {
      page: 1,
      perPage: 20,
      search: searchPipeline,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchPipeline = async (value: any) => {
    setSearchPipeline(value)
    await pipelineList.mutate()
  }

  const distributionChannelsList = useAxios<
    PaginationResponseInterface<IDistributionChannel>
  >({
    method: 'GET',
    url: '/distribution-channels',
    params: {
      page: 1,
      perPage: 5,
      name: searchDistributionChannel,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchDistributionChannel = async (value: string) => {
    setSearchDistributionChannel(value)
    await distributionChannelsList.mutate()
  }

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    formik.setFieldValue('file', file)
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        const wb = read(event.target!.result)
        const sheets = wb.SheetNames

        if (sheets.length) {
          const rows: any[] = utils.sheet_to_json(wb.Sheets[sheets[0]])
          const lowerCaseKeys = rows.map((row) => {
            const lowerCaseRow: ILeadExcel | any = {}
            for (const key in row) {
              lowerCaseRow[key.toLowerCase()] = row[key]
            }
            return lowerCaseRow
          })
          setLeadExcelList(lowerCaseKeys)
        }
      }
      reader.readAsArrayBuffer(file)
    }
  }

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="lg">
      <CircularLoading message={popupMessage} isLoading={isLoading} />
      {/* <CircularLoading isLoading={isLoading} /> */}
      <DialogTitle>นำเข้าทะเบียนลูกค้า</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={6}>
              {/* <SelectSearch<IPipeline>
                label={'เลือก pipeline'}
                onSelect={(e) =>
                  e && formik.setFieldValue('pipeline', e.value.objectId)
                }
                search={searchPipeline}
                onSearch={handleSearchPipeline}
                options={
                  pipelineList.data?.records.map((pipeline: IPipeline) => ({
                    label: pipeline.name,
                    value: pipeline,
                  })) || ([] as IOptionSelect<IPipeline>[])
                }
              /> */}
              <PipelineSelectSearch
                multiple={false}
                onSelect={(e) =>
                  e && formik.setFieldValue('pipeline', e.value.objectId)
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              {/* <SelectSearch<IDistributionChannel>
                label={'เลือกช่องทาง'}
                onSelect={(e) =>
                  e &&
                  formik.setFieldValue('distributionChannel', e?.value.objectId)
                }
                search={searchDistributionChannel}
                onSearch={handleSearchDistributionChannel}
                options={
                  distributionChannelsList.data?.records.map(
                    (distributionChannel: IDistributionChannel) => ({
                      label: distributionChannel.name,
                      value: distributionChannel,
                    }),
                  ) || ([] as IOptionSelect<IDistributionChannel>[])
                }
              /> */}
              <ChannelSelectSearch
                multiple={false}
                label={'เลือกช่องทางการขาย'}
                onSelect={(e: any) =>
                  e &&
                  formik.setFieldValue('distributionChannel', e?.value.objectId)
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="expiredDays"
                fullWidth
                label="จำนวนวันที่หมดอายุ"
                name="expiredDays"
                value={formik.values.expiredDays}
                onBlur={formik.handleBlur}
                onKeyPress={(e) => {
                  if (['+', '-', 'e', '*', '.'].includes(e.key)) {
                    e.preventDefault()
                  }
                }}
                onChange={(e) =>
                  formik.setFieldValue(
                    'expiredDays',
                    validateMinMaxValue(e.target.value, 0, 1000) || '',
                  )
                }
                helperText={
                  formik.touched.expiredDays &&
                  (formik.errors.expiredDays as string)
                }
                error={
                  !!(formik.touched.expiredDays && formik.errors.expiredDays)
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="ignoreBlacklist"
                fullWidth
                label="รายชื่อ Blacklist"
                name="ignoreBlacklist"
                value={formik.values.ignoreBlacklist}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                helperText={
                  formik.touched.ignoreBlacklist &&
                  (formik.errors.ignoreBlacklist as string)
                }
                error={
                  !!(
                    formik.touched.ignoreBlacklist &&
                    formik.errors.ignoreBlacklist
                  )
                }
                select
              >
                <MenuItem value="true">นำเข้ายกเว้นรายชื่อ Blacklist</MenuItem>
                <MenuItem value="false">นำเข้าทั้งหมด</MenuItem>
              </TextField>
            </Grid>
            <Grid
              item
              xs={12}
              lg={12}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  maxWidth: '400px',
                  height: '50px',
                }}
              >
                <input
                  style={{
                    padding: '10px',
                  }}
                  type="file"
                  name="file"
                  className="custom-input-file"
                  required
                  onChange={handleImport}
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  maxWidth: '550px',
                  minWidth: '300px',
                }}
              >
                <Typography variant="body1">
                  หมายเหตุ
                  <Typography variant="body2" color="text.secondary">
                    ช่อง Email, AddBy, Facebook และ Channel ไม่จำเป็นต้องระบุ
                  </Typography>
                </Typography>

                <Button
                  variant="outlined"
                  color="info"
                  size="large"
                  startIcon={<GridOnIcon fontSize="large" />}
                  component={'a'}
                  href="/excels/import-lead.xlsx"
                  // onClick={() => {
                  //   downloadExcel()
                  // }}
                >
                  ดาวน์โหลดตัวอย่างไฟล์
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                บันทึก
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
